import { v4 as uuid4 } from 'uuid';

import type { UserInput } from '../services/queryClient/useMutateLogin';
import type { SignupInput } from '../services/queryClient/useMutateSignup';
import type { Country, PortalUser } from '../types/user';
import type { MFAInfoResult, MFATokenResult } from '../types/mfa';
import { Config } from '../services/Config';
import type { Response } from '../types/response';

type GaMfaInitProps = {
  token: string;
};

type MfaEnableOrVerifyProps = {
  code: string;
  token: string;
};

type MicrosoftSignupProps = {
  token: string;
  email: string;
  region?: Country;
};

type MicrosoftLoginProps = MicrosoftSignupProps;

export type GoogleSignupProps = MicrosoftSignupProps & { redirectURL: string };
export type GoogleLoginProps = GoogleSignupProps;

type revokeMfaWithPinProps = {
  pin: string;
  token: string;
};

type updateMfaPinProps = {
  pin: string;
  newPin: string;
  token: string;
};

type sendSmsMfaCodeProps = {
  smsMfaPhone?: string;
  token: string;
};

type updatePortalPasswordProps = {
  userid: string;
  ott: string;
  password: string;
};

type updateIdentityPortalPasswordProps = {
  userName: string;
  resetCode: string;
  newPassword: string;
  clientId: string;
};

type changePasswordProps = {
  token: string;
  oldPassword: string;
  newPassword: string;
};

type MFAResignProps = {
  mfaToken: string;
  accessToken: string;
};

export async function login(
  userInput: UserInput
): Promise<(PortalUser & MFAInfoResult) | PortalUser | Response> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/login/`,
      {
        method: 'POST',
        body: JSON.stringify(userInput),
        headers: {
          'x-rkn-correlation-id': uuid4(),
        },
      }
    );
    return response.json();
  } catch (ex) {
    throw ex;
  }
}

export async function signup(
  signupInput: SignupInput
): Promise<PortalUser | Response> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/users/signup/`,
      {
        method: 'POST',
        body: JSON.stringify(signupInput),
        headers: {
          'x-rkn-correlation-id': uuid4(),
        },
      }
    );
    return response.json();
  } catch (ex) {
    throw ex;
  }
}

export async function mfaResignToken(
  userToken: MFAResignProps
): Promise<MFATokenResult> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/mfa/resigntoken`,
      {
        method: 'POST',
        body: JSON.stringify(userToken),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: userToken.accessToken,
        },
      }
    );
    const respJson = await response.json();
    if (respJson.result === 'error') {
      throw new Error(respJson.message);
    }
    return { ...respJson, mfaToken: userToken.mfaToken };
  } catch (ex) {
    throw ex;
  }
}

export async function mfaInfo(
  accessToken: string
): Promise<MFAInfoResult | Response> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/mfa/info`,
      {
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: accessToken,
        },
      }
    );
    return response.json();
  } catch (ex) {
    throw ex;
  }
}

export async function updatePortalPassword({
  userid,
  ott,
  password,
}: updatePortalPasswordProps): Promise<void> {
  try {
    await fetch(`${Config.APPLICATION_AUTH_BACKEND}/authv2/password/reset`, {
      method: 'PUT',
      body: JSON.stringify({ userid, token: ott, password }),
    });
  } catch (ex) {}
}

export async function updateIdentityPortalPassword({
  userName,
  resetCode,
  newPassword,
  clientId,
}: updateIdentityPortalPasswordProps): Promise<void> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/v2/password/reset`,
      {
        method: 'POST',
        body: JSON.stringify({
          userEmail: userName,
          code: resetCode,
          password: newPassword,
          clientId,
        }),
      }
    );
    const respJson = await response.json();
    if (respJson.result === 'error') {
      throw new Error(respJson.message);
    }
    return respJson;
  } catch (ex) {
    throw ex;
  }
}

export async function changePassword({
  token,
  oldPassword,
  newPassword,
}: changePasswordProps): Promise<void> {
  await fetch(`${Config.APPLICATION_AUTH_BACKEND}/authv2/password/change`, {
    method: 'POST',
    headers: {
      'x-rkn-correlation-id': uuid4(),
      authorizationtoken: token,
    },
    body: JSON.stringify({ oldPassword, newPassword }),
  });
}

export async function tokenValidate(token: string): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/tokenvalidate`,
      {
        method: 'GET',
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
        },
      }
    );
    const respJson = await response.json();
    if (respJson.result === 'error') {
      throw new Error(respJson.message);
    }
    return respJson;
  } catch (ex) {
    throw ex;
  }
}

// SMS && GA
export async function validateMfaToken(
  mfaToken: string,
  token: string
): Promise<Response | { result: 'success' }> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/mfa/tokenvalidate?mfaToken=${mfaToken}`,
      {
        method: 'GET',
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
        },
      }
    );
    return response.json();
  } catch (ex) {
    throw ex;
  }
}

export async function GAMFAInit({
  token,
}: GaMfaInitProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/mfa`,
      {
        method: 'POST',
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
        },
      }
    );
    const result: any = await response.json();
    if (response.status < 300) {
      return { qrCodeUrl: result.qrCodeUrl, qrCode: result.qrCode };
    } else if (400 <= response.status && response.status < 500) {
      throw new Error(result.message);
    } else if (500 <= response.status) {
      throw new Error('mfa service failed');
    }
  } catch (ex) {
    throw ex;
  }
}

// GA
export async function GAMFAVerify({
  code,
  token,
}: MfaEnableOrVerifyProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/mfa/verify`,
      {
        method: 'PUT',
        body: JSON.stringify({ verifyCode: code }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

export async function GAMFAEnable({
  code,
  token,
}: MfaEnableOrVerifyProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/mfa/enable`,
      {
        method: 'PUT',
        body: JSON.stringify({ verifyCode: code }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

export async function microsoftLogin({
  token,
  email,
  region,
}: MicrosoftLoginProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/login/microsoft`,
      {
        method: 'POST',
        body: JSON.stringify({ idToken: token, country: region }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          // 'app-id': clientId,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      if (resp.mfaEnabled) {
        return mfaInfo(resp.token).then((mfaData) => {
          return {
            ...resp,
            ...mfaData,
          };
        });
      } else {
        return resp;
      }
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

export async function microsoftSignup({
  token,
  email,
  region,
}: MicrosoftSignupProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/signup/microsoft`,
      {
        method: 'POST',
        body: JSON.stringify({ idToken: token, country: region }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          // 'app-id': clientId,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

export async function googleSignup({
  token,
  email,
  region,
}: GoogleSignupProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/signup/google`,
      {
        method: 'POST',
        body: JSON.stringify({ idToken: token, country: region }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          // 'app-id': clientId,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

export async function googleLogin({
  token,
  email,
  region,
}: GoogleSignupProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/login/google`,
      {
        method: 'POST',
        body: JSON.stringify({ idToken: token, country: region }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          // 'app-id': clientId,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      if (resp.mfaEnabled) {
        return mfaInfo(resp.token).then((mfaData) => {
          return {
            ...resp,
            ...mfaData,
          };
        });
      } else {
        return resp;
      }
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

// SMS MFA
export async function SmsMfaEnable({
  code,
  token,
}: MfaEnableOrVerifyProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/sms/mfa/verify`,
      {
        method: 'POST',
        body: JSON.stringify({ smsMfaCode: code }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

// SMS MFA
export async function verifyMFACode({
  code,
  token,
}: MfaEnableOrVerifyProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/sms/mfa/verify`,
      {
        method: 'POST',
        body: JSON.stringify({ smsMfaCode: code }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
        },
      }
    );
    return response.json();
  } catch (ex) {
    throw ex;
  }
}

// SMS & GA ??
export async function revokeMfaWithPin({ pin, token }: revokeMfaWithPinProps) {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/mfa/recovery`,
      {
        method: 'DELETE',
        body: JSON.stringify({ pin }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(
        typeof resp.message === 'object'
          ? `${resp.message.error} Remaining ${resp.message.remainingAttempts} attempts.`
          : resp.message
      );
    }
  } catch (ex) {
    throw ex;
  }
}

// SMS & GA??
export async function updateMFAPin({
  pin,
  newPin,
  token,
}: updateMfaPinProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/mfa/recovery`,
      {
        method: 'PUT',
        body: JSON.stringify({ pin, newPin }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          Accept: 'application/json',
          'Content-Type': 'application/json',
          authorizationtoken: token,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

// SMS
export async function sendMFACode({
  smsMfaPhone,
  token,
}: sendSmsMfaCodeProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/sms/mfa/send`,
      {
        method: 'POST',
        body: JSON.stringify({ ...(smsMfaPhone ? { smsMfaPhone } : {}) }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          Accept: 'application/json',
          'Content-Type': 'application/json',
          authorizationtoken: token,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

export async function checkExistingEmail(email: string, validity: string) {
  if (validity === 'invalid') {
    return Promise.reject();
  }
  const resp = await fetch(
    `${Config.APPLICATION_AUTH_BACKEND}/auth/email/${encodeURIComponent(
      email
    )}`,
    {
      method: 'GET',
      headers: {
        'x-rkn-correlation-id': uuid4(),
      },
    }
  );
  if (!resp.ok) {
    throw new Error('Email check failed');
  }
  return resp.json();
}

export async function forgotPassword(userid: string): Promise<MFATokenResult> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/v2/password/forgot`,
      {
        method: 'POST',
        body: JSON.stringify({ userid }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
        },
      }
    );
    const respJson = await response.json();
    if (respJson.result === 'error') {
      throw new Error(respJson.message);
    }
    return respJson;
  } catch (ex) {
    throw ex;
  }
}

export async function VerifyUser(
  email: string,
  userid: string,
  verifyToken: string
): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${Config.APPLICATION_AUTH_BACKEND}/auth/verifyuser?email=${email}&userid=${userid}&verify=${verifyToken}`,
      {
        method: 'GET',
        headers: {
          'x-rkn-correlation-id': uuid4(),
        },
      }
    );
    return response.json();
  } catch (ex) {
    throw ex;
  }
}
